import { useState, useMemo, useRef } from 'react';

import { useDebounce } from 'use-debounce';
import { useOnClickOutside } from 'usehooks-ts';

import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { cn } from '@/helpers/shadCnUtils';
import { Icon } from '@/new/components/icon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface DocumentTypeListProps {
  title: string;
  documentTypes: string[];
  onClear: () => void;
  onRemove: (type: string) => void;
}

const DocumentTypeList = ({ title, documentTypes, onClear, onRemove }: DocumentTypeListProps) => {
  return (
    <div
      className={cn(
        'h-24 overflow-y-auto rounded border-gray-200 bg-white transition-all duration-200',
        documentTypes.length === 0 ? 'h-0' : 'my-2 h-24 border',
      )}
    >
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-2 py-1.5">
        <div className="flex items-center gap-1 text-xs font-medium text-blue-700">
          <Icon className="lucide--file-type" />

          <span>{title}</span>
        </div>
        <button onClick={onClear} className="text-xs text-gray-500 hover:text-gray-700">
          Clear all
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 p-2">
        {documentTypes.length > 100 ? (
          <div className="flex w-full justify-center p-2 text-xs text-gray-500">
            {documentTypes.length} document types...
          </div>
        ) : (
          documentTypes.map((type) => (
            <div key={type} className="flex items-center gap-1 rounded bg-blue-50 px-2 py-1 text-xs text-blue-700">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="max-w-36 truncate capitalize">{capitalizeFirstLetter(type)}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs capitalize">{capitalizeFirstLetter(type)}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <button
                onClick={() => onRemove(type || '')}
                className="ml-0.5 flex items-center justify-center rounded p-0.5 text-blue-700 hover:bg-blue-100"
              >
                <Icon className="lucide--x h-3 w-3" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

interface DocumentTypeFilterProps {
  selectedTypes: string[];
  setSelectedTypes: (types: string[]) => void;
  allDocumentTypes: string[];
}

export const DocumentTypeFilter = ({ selectedTypes, setSelectedTypes, allDocumentTypes }: DocumentTypeFilterProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const dropdownRef = useRef(null);

  useOnClickOutside(dropdownRef, () => {
    setSearchTerm('');
    setIsSearchFocused(false);
  });

  const filteredDocumentTypes = useMemo(() => {
    if (!isSearchFocused && !searchTerm) return [];

    const searchLower = debouncedSearchTerm.toLowerCase();
    return allDocumentTypes
      .filter((type) => type.toLowerCase().includes(searchLower))
      .map((v) => (v == '' ? 'Unassigned' : v));
  }, [allDocumentTypes, debouncedSearchTerm, searchTerm, isSearchFocused]);

  const totalCount = filteredDocumentTypes.length;

  const selectedTypesSet = useMemo(() => new Set(selectedTypes), [selectedTypes]);

  const handleSelectAll = () => {
    const newTypes = filteredDocumentTypes.filter((type) => !selectedTypesSet.has(type));

    setSelectedTypes([...selectedTypes, ...newTypes]);
    setSearchTerm('');
    setIsSearchFocused(false);
  };

  const handleToggleType = (type: string) => {
    if (selectedTypesSet.has(type)) {
      // Remove it
      setSelectedTypes(selectedTypes.filter((t) => t !== type));
    } else {
      // Add it
      setSelectedTypes([...selectedTypes, type]);
    }

    if (filteredDocumentTypes.length === 1) {
      setSearchTerm('');
      setIsSearchFocused(false);
    }
  };

  return (
    <>
      <div className="flex w-full items-center justify-start gap-2">
        <div className="flex w-full flex-col gap-2">
          <div className="relative w-full">
            <div className="flex gap-2">
              <div className="relative w-full">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  placeholder="Search document types..."
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-xs focus:border-black focus:outline-none"
                />
                <Icon className="lucide--search absolute top-1/2 right-2 h-3 w-3 -translate-y-1/2 text-gray-500" />
              </div>
            </div>
            {isSearchFocused && filteredDocumentTypes.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute z-10 mt-1 max-h-42 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
              >
                <div className="sticky top-0 border-b bg-gray-50 px-3 py-1.5">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      <span>{totalCount}</span> document type{totalCount !== 1 ? 's' : ''} found
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={handleSelectAll}
                        className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-0.5 text-xs text-gray-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                        title="Select all matching document types"
                      >
                        <Icon className="lucide--check-circle h-3 w-3" />
                        Select {totalCount}
                      </button>
                    </div>
                  </div>
                </div>
                {filteredDocumentTypes.map((type) => {
                  const isSelected = selectedTypesSet.has(type);
                  return (
                    <div
                      key={type}
                      className={cn(
                        'group flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 hover:bg-gray-100',
                        isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'bg-white',
                      )}
                      onClick={() => handleToggleType(type)}
                    >
                      <span className="flex-1 truncate text-xs capitalize">{type}</span>
                      {isSelected && <Icon className="lucide--check-circle-2 h-4 w-4 text-blue-500" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <DocumentTypeList
        title={`Selected ${selectedTypes.length} document type${selectedTypes.length !== 1 ? 's' : ''}`}
        documentTypes={selectedTypes}
        onClear={() => {
          setSelectedTypes([]);
        }}
        onRemove={(typeValue) => {
          setSelectedTypes(selectedTypes.filter((t) => t !== typeValue));
        }}
      />
    </>
  );
};
